import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Layers, Search, Mail, Clock, ShieldAlert, History, Settings, LogOut, Menu, AtSign } from "lucide-react";
import { useOutlookStatus } from "@/components/OutlookConnect";

const LOGO = "https://framerusercontent.com/images/kXzfcOIGDr33MzqF8NWQisPPw.png?width=677&height=832";

function OutlookDot() {
  const [st] = useOutlookStatus();
  if (!st) return null;
  return (
    <NavLink to="/outlook" data-testid="sidebar-outlook-status" className="mx-3 mb-2 flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs transition-colors hover:border-primary/40">
      <span className={`h-2 w-2 rounded-full ${st.connected ? "bg-emerald-600" : "bg-primary animate-pulse"}`} />
      <span className="truncate">{st.connected ? st.mailbox : "Sign in to Outlook"}</span>
    </NavLink>
  );
}
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  ["overview", "Overview", LayoutDashboard, "/overview"],
  ["tracks", "Tracks", Layers, "/tracks"],
  ["prospects", "Prospects", Search, "/prospects"],
  ["drafts", "Draft Queue / Outbox", Mail, "/drafts"],
  ["followups", "Follow-ups", Clock, "/followups"],
  ["suppression", "Suppression List", ShieldAlert, "/suppression"],
  ["runs", "Research Runs", History, "/research-runs"],
  ["outlook", "Outlook", AtSign, "/outlook"],
  ["settings", "Settings", Settings, "/settings"],
];

export function QuotaPill() {
  const [q, setQ] = useState(null);
  const loc = useLocation();
  useEffect(() => {
    const load = () => api.get("/quota").then((r) => setQ(r.data)).catch(() => {});
    load();
    window.addEventListener("kh-quota", load);
    return () => window.removeEventListener("kh-quota", load);
  }, [loc.pathname]);
  if (!q) return null;
  const pct = Math.min(100, (q.used / q.cap) * 100);
  return (
    <div className="flex items-center gap-3 rounded-full border bg-card px-4 py-1.5" data-testid="daily-quota-pill">
      <span className="kh-label">Today</span>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary">
        <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs" data-testid="daily-quota-value">{q.used}/{q.cap} drafts</span>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <div className="flex min-h-screen">
      {open && <div className="fixed inset-0 z-20 bg-[#211B16]/40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r bg-[hsl(var(--sidebar))] transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="flex items-center gap-3 px-5 pb-6 pt-6">
          <img src={LOGO} alt="Khanata" className="h-12 w-auto" />
          <div>
            <div className="font-display text-2xl leading-none text-[hsl(var(--brown))]" data-testid="brand-logo">Khanata</div>
            <div className="font-sc mt-1 text-[11px] text-[hsl(var(--brown))]">outreach portal</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3">
          {NAV.map(([id, label, Icon, path]) => (
            <NavLink key={id} to={path} data-testid={`nav-${id}`}
              className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
              {({ isActive }) => (<>
                <Icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} strokeWidth={1.75} />
                <span>{label}</span>
                {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
              </>)}
            </NavLink>
          ))}
        </nav>
        <OutlookDot />
        <div className="m-3 mt-0 rounded-xl border bg-card p-4">
          <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
          <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          <button onClick={logout} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-primary">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 lg:ml-64">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between gap-3 border-b bg-background/80 px-4 backdrop-blur-xl sm:px-8">
          <button className="rounded-md p-2 hover:bg-accent lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="kh-label hidden sm:block">Manual review · No automatic sending</div>
          <QuotaPill />
        </header>
        <div className="mx-auto max-w-7xl space-y-8 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
